import { useEffect, useMemo, useRef, useState } from 'react';
import { Trophy } from 'lucide-react';
import { fetchWinnersToday } from '../lib/api';

// Perceived scroll speed (pixels per second). Higher = faster.
const PIXELS_PER_SECOND = 90;

export default function WelderOfDayTape() {
  const [winners, setWinners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const copyRef = useRef(null);
  const [duration, setDuration] = useState(20);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchWinnersToday();
        if (!mounted) return;
        setWinners(data?.winners || []);
      } catch (e) {
        if (mounted) setWinners([]);
      } finally {
        if (mounted) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  // After render, measure one copy's width and compute the animation duration
  // so that the perceived scroll speed stays constant regardless of list length.
  useEffect(() => {
    if (!copyRef.current || winners.length === 0) return;
    const w = copyRef.current.getBoundingClientRect().width;
    if (w > 0) setDuration(Math.max(10, w / PIXELS_PER_SECOND));
  }, [winners]);

  const list = useMemo(() => winners, [winners]);

  return (
    <div className="sticky top-0 z-50 relative bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-[#0b0b0d] overflow-hidden">
      <div className="flex items-stretch">
        <div className="shrink-0 flex items-center gap-2 pl-3 sm:pl-4 pr-3 sm:pr-5 py-2.5 bg-[#0b0b0d] text-amber-400 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-semibold border-r-2 border-amber-500">
          <Trophy className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden xs:inline">Welders of the Day</span>
          <span className="xs:hidden">Top&nbsp;Welders</span>
        </div>
        <div className="relative flex-1 overflow-hidden">
          {list.length > 0 ? (
            <div
              className="flex w-max animate-marquee"
              style={{ animationDuration: `${duration}s` }}
            >
              <MarqueeCopy items={list} refFirst={copyRef} />
              <MarqueeCopy items={list} aria-hidden />
            </div>
          ) : (
            <div className="py-2.5 px-4 text-xs sm:text-sm font-medium text-[#0b0b0d]/80 whitespace-nowrap overflow-hidden text-ellipsis">
              {loaded
                ? 'Scan a Prakash Electrodes product code today to earn cashback and appear here!'
                : 'Loading today’s winners…'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MarqueeCopy({ items, refFirst }) {
  return (
    <div
      ref={refFirst}
      className="flex items-center gap-6 sm:gap-10 pr-6 sm:pr-10 py-2.5 whitespace-nowrap shrink-0"
    >
      {items.map((w, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium"
        >
          <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-wider bg-[#0b0b0d] text-amber-400 px-1.5 py-0.5 rounded shrink-0">
            #{i + 1}
          </span>
          <span className="font-semibold">{w.welder_name}</span>
          {w.welder_city && (
            <span className="text-[#0b0b0d]/70">• {w.welder_city}</span>
          )}
        </span>
      ))}
    </div>
  );
}
